import React from 'react';
import { Download, Loader2, RefreshCw, ShieldCheck, GraduationCap, MapPin, Briefcase, Award, UserCheck, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { fetchResumeBlob, fetchUploadedResumeBlob } from '../../api';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlight = ({ text, words }) => {
  if (!words?.length) return text;
  const rx = new RegExp(`((?<![A-Za-z0-9])(?:${words.map(escapeRe).join('|')})(?![A-Za-z0-9]))`, 'gi');
  const lower = new Set(words.map(w => w.toLowerCase()));
  return text.split(rx).map((part, i) => lower.has(part.toLowerCase())
    ? <mark key={i} className="bg-cyan-400/25 text-cyan-100 rounded px-0.5">{part}</mark> : part);
};

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-xs text-slate-300 bg-slate-800/70 border border-slate-700 rounded-full px-2 py-0.5"><Icon className="w-3 h-3 text-cyan-400" />{children}</span>
);

export const ResumeResultCard = ({ r, onReindex, onHired }) => {
  const p = r.profile;
  const download = () => (r.source === 'application' ? fetchResumeBlob : fetchUploadedResumeBlob)(r.ref_id, r.resume_filename);
  const pending = ['processing', 'queued'].includes(r.status) || !r.status;

  return (
    <div data-testid={`resume-result-${r.ref_id}`} className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-5 hover:border-cyan-400/30 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-lg font-semibold text-white">{r.candidate_name || 'Unnamed candidate'}</h4>
          <p className="text-xs text-slate-500 mt-0.5">
            {r.source === 'application' ? 'Applied on website' : 'Imported'}
            {r.job_title ? <> &middot; {r.job_title}</> : <> &middot; No job linked</>}
            {r.created_at && <> &middot; {new Date(r.created_at.endsWith('Z') ? r.created_at : r.created_at + 'Z').toLocaleDateString()}</>}
            {r.candidate_email && <> &middot; {r.candidate_email}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {r.matched_terms?.length > 0 && <span data-testid={`resume-result-hits-${r.ref_id}`} className="text-xs text-cyan-300">{r.score} match{r.score === 1 ? '' : 'es'}</span>}
          {r.hired ? (
            <>
              <span data-testid={`resume-hired-badge-${r.ref_id}`} className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">Hired{r.hired_at ? ` ${new Date(r.hired_at.endsWith('Z') ? r.hired_at : r.hired_at + 'Z').toLocaleDateString()}` : ''}</span>
              <Button data-testid={`resume-unhire-${r.ref_id}`} onClick={() => onHired(r, false)} variant="outline" className="h-8 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>
            </>
          ) : (
            <Button data-testid={`resume-hire-${r.ref_id}`} onClick={() => onHired(r, true)} variant="outline" className="h-8 border-emerald-600/50 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"><UserCheck className="w-3.5 h-3.5 mr-1" />Hired</Button>
          )}
          <Button data-testid={`resume-download-${r.ref_id}`} onClick={download} variant="outline" className="h-8 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"><Download className="w-3.5 h-3.5 mr-1" />Resume</Button>
        </div>
      </div>

      {p ? (
        <>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Chip icon={ShieldCheck}>{p.clearance === 'Unknown' ? 'Clearance not stated' : p.clearance === 'None' ? 'No clearance' : p.clearance}</Chip>
            {p.years_experience != null && <Chip icon={Briefcase}>{p.years_experience} yrs</Chip>}
            {p.education_level && p.education_level !== 'Unknown' && <Chip icon={GraduationCap}>{p.education_level}</Chip>}
            {p.location && <Chip icon={MapPin}>{p.location}</Chip>}
            {p.certifications?.map(c => <Chip key={c} icon={Award}>{c}</Chip>)}
          </div>
          {p.summary && <p className="text-sm text-slate-400 mt-3">{p.summary}</p>}
        </>
      ) : (
        <p className="text-xs mt-3 text-slate-500 flex items-center gap-2">
          {pending ? <><Loader2 className="w-3 h-3 animate-spin" />Reading resume details...</>
            : r.status === 'no_text' ? 'No readable text found in this file (it may be a scanned image).'
            : <>Couldn't read details. <button data-testid={`resume-reindex-${r.ref_id}`} onClick={() => onReindex(r.id)} className="text-cyan-400 hover:underline inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" />Retry</button></>}
        </p>
      )}

      {r.snippets?.length > 0 && (
        <div className="mt-3 space-y-1.5 border-l-2 border-cyan-400/30 pl-3">
          {r.snippets.map((s, i) => <p key={i} className="text-xs text-slate-300 leading-relaxed"><Highlight text={s} words={r.highlight_words} /></p>)}
        </div>
      )}
    </div>
  );
};

export default ResumeResultCard;
